// studentapps/Dashboard.js
import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  Avatar,
  AvatarGroup,
  Divider
} from "@mui/material";

import {
  TrendingUp,
  Assignment,
  Schedule,
  Star,
  Group,
  EmojiEvents,
  CorporateFare
} from "@mui/icons-material";
import FloatingHomeReturn from "../components/FloatingHomeReturn";

/* ---------- STATIC DATA ---------- */

const stats = [
  { label: "Problems Solved", value: "24", icon: <TrendingUp />, color: "primary", progress: 80 },
  { label: "Pending Tasks", value: "5", icon: <Assignment />, color: "secondary", progress: 60 },
  { label: "Study Hours", value: "18.5", icon: <Schedule />, color: "info", progress: 75 },
  { label: "Notes Created", value: "12", icon: <Star />, color: "success", progress: 90 }
];

const weeklyProgress = [
  { title: "Problem Solving", value: 75 },
  { title: "Study Time", value: 60 },
  { title: "Task Completion", value: 45 },
  { title: "Note Taking", value: 85 }
];

const recentActivities = [
  { action: "Solved Calculus Problem", time: "2 hours ago", subject: "Math" },
  { action: "Created Physics Notes", time: "5 hours ago", subject: "Physics" },
  { action: "Completed Study Session", time: "1 day ago", subject: "Chemistry" },
  { action: "Added New Task", time: "2 days ago", subject: "General" }
];

/* ---------- COMPONENT ---------- */

const Dashboard = () => {
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ---------- HEADER ---------- */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 4,
          background: "linear-gradient(135deg, #0a1128, #1a1f3d)",
          color: "white"
        }}
      >
        <FloatingHomeReturn/>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" fontWeight={700}>
              Welcome Back, Student 👋
            </Typography>
            <Typography sx={{ opacity: 0.85 }}>
              Track your weekly learning progress at a glance
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign={{ xs: "left", md: "right" }}>
            <Chip
              icon={<CorporateFare />}
              label="Zolang Nexora"
              sx={{
                bgcolor: "rgba(255,255,255,0.15)",
                color: "white",
                fontWeight: 600
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ---------- STATS CARDS ---------- */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: "100%", borderRadius: 3 }}>
              <CardContent>
                <Avatar
                  sx={{
                    bgcolor: `${item.color}.main`,
                    mb: 2,
                    width: 48,
                    height: 48
                  }}
                >
                  {item.icon}
                </Avatar>

                <Typography variant="h4" fontWeight={700}>
                  {item.value}
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  {item.label}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={item.progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mt: 1
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ---------- MAIN CONTENT ---------- */}
      <Grid container spacing={3}>

        {/* LEFT COLUMN */}
        <Grid item xs={12} md={8}>

          {/* Weekly Progress */}
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Weekly Progress
              </Typography>

              {weeklyProgress.map((item, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>{item.title}</Typography>
                    <Typography color="text.secondary">{item.value}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.value}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recent Activities
              </Typography>

              <List>
                {recentActivities.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText
                        primary={activity.action}
                        secondary={activity.time}
                      />
                      <Chip size="small" label={activity.subject} />
                    </ListItem>
                    {index !== recentActivities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              <Button variant="outlined" fullWidth sx={{ mt: 2 }}>
                View All Activities
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={4}>

          {/* Study Streak */}
          <Card sx={{ mb: 3, borderRadius: 3, textAlign: "center" }}>
            <CardContent>
              <EmojiEvents sx={{ fontSize: 48, color: "warning.main" }} />
              <Typography variant="h3" fontWeight={700}>
                7
              </Typography>
              <Typography color="text.secondary">
                Day Study Streak
              </Typography>
            </CardContent>
          </Card>

          {/* Study Buddies */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Study Buddies
              </Typography>

              <AvatarGroup max={4} sx={{ justifyContent: "center", my: 2 }}>
                <Avatar />
                <Avatar />
                <Avatar />
                <Avatar />
              </AvatarGroup>

              <Button
                variant="contained"
                fullWidth
                startIcon={<Group />}
              >
                Join Study Group
              </Button>
            </CardContent>
          </Card>

        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
